/**
 * Core diagnostic logger for Twilio Video SDK events.
 *
 * Toggle detection: diagnostics are active when:
 *   (a) window.location.search contains "debug=twilio", OR
 *   (b) localStorage.getItem('immerss_debug') === 'twilio', OR
 *   (c) window.location.hostname is (a subdomain of) luxury-appointments.pages.dev
 *       — auto-enables on the Cloudflare Pages UAT host and its preview/branch
 *       subdomains so internal testers always produce a diagnosable dump without
 *       toggling manually. Production (custom domain) does not match and stays OFF.
 * All three sources are checked in isDiagEnabled(). localStorage access is wrapped
 * in a try/catch — it can throw in sandboxed iframes; catch treats it as false.
 *
 * Disconnect resets buffer: [twilio:room:disconnect] is pushed to the ring buffer
 * first, then resetBuffer() clears it so that entry becomes the last one retained.
 * Subsequent sessions start with a clean buffer.
 *
 * attachRoomLogger(room) is idempotent: a module-private WeakSet<Room> tracks which
 * Room instances have already been instrumented. A second call on the same Room is
 * a no-op and returns a no-op detach function.
 */

import type { Room } from 'twilio-video';
import { RingBuffer } from './ring-buffer';
import type { LogEntry } from './types';

// Module-private ring buffer — 500-slot capacity
const buffer = new RingBuffer<LogEntry>(500);

// Tracks Room instances that have already been instrumented
const attachedRooms = new WeakSet<Room>();

// ─── Toggle detection ────────────────────────────────────────────────────────

export function isDiagEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // (a) URL query param
  if (window.location.search.includes('debug=twilio')) return true;

  // (b) localStorage — wrapped to survive sandboxed iframes
  try {
    if (localStorage.getItem('immerss_debug') === 'twilio') return true;
  } catch {
    // localStorage access threw (sandboxed iframe or SecurityError) — treat as false
  }

  // (c) UAT host auto-enable: the Pages hostname and its UAT custom domain
  // (production custom domain does not match)
  const host = window.location.hostname || '';
  if (
    host === 'luxury-appointments.pages.dev' ||
    host.endsWith('.luxury-appointments.pages.dev') ||
    host === 'virtualappointments-uat.immrs.in'
  ) {
    return true;
  }

  return false;
}

// ─── Core log / buffer ───────────────────────────────────────────────────────

export function log(tag: string, payload: Record<string, unknown>): void {
  if (!isDiagEnabled()) return;
  const entry: LogEntry = { tag, payload };
  buffer.push(entry);
  console.log(tag, payload);
}

export function snapshot(): LogEntry[] {
  if (!isDiagEnabled()) return [];
  return buffer.snapshot();
}

export function resetBuffer(): void {
  buffer.clear();
}

// ─── attachRoomLogger ────────────────────────────────────────────────────────

/**
 * Attaches all Twilio SDK event listeners to the given Room, emitting structured
 * diagnostic log entries. Returns a detach function that removes all attached
 * listeners. Idempotent: calling twice on the same Room is a no-op.
 */
export function attachRoomLogger(room: Room): () => void {
  if (!isDiagEnabled()) return () => undefined;

  // Idempotency guard
  if (attachedRooms.has(room)) return () => undefined;
  attachedRooms.add(room);

  const cleanups: Array<() => void> = [];

  function on<T extends object>(
    emitter: T & { on(event: string, cb: (...args: any[]) => void): any; off(event: string, cb: (...args: any[]) => void): any },
    event: string,
    cb: (...args: any[]) => void
  ): void {
    emitter.on(event, cb);
    cleanups.push(() => emitter.off(event, cb));
  }

  const roomSid = room.sid ?? null;
  const localSid = room.localParticipant?.sid ?? null;

  // ── [twilio:room:connect] — emitted immediately on attach ──────────────────
  log('[twilio:room:connect]', {
    t: Date.now(),
    roomSid,
    roomName: room.name ?? null,
    localParticipantSid: localSid,
  });

  // ── [twilio:room:disconnect] ───────────────────────────────────────────────
  const onDisconnected = (disconnectedRoom: Room, error?: { message?: string; code?: number }) => {
    log('[twilio:room:disconnect]', {
      t: Date.now(),
      roomSid: disconnectedRoom.sid ?? null,
      roomName: disconnectedRoom.name ?? null,
      localParticipantSid: disconnectedRoom.localParticipant?.sid ?? null,
      reason: error?.message ?? (error?.code != null ? String(error.code) : undefined),
    });
    // Per spec: disconnect event is pushed first, then buffer is reset
    resetBuffer();
  };
  on(room as any, 'disconnected', onDisconnected);

  // ── [twilio:room:reconnecting] ─────────────────────────────────────────────
  const onReconnecting = (error?: { message?: string }) => {
    log('[twilio:room:reconnecting]', {
      t: Date.now(),
      roomSid,
      roomName: room.name ?? null,
      localParticipantSid: localSid,
      reason: error?.message,
    });
  };
  on(room as any, 'reconnecting', onReconnecting);

  // ── [twilio:room:reconnected] ──────────────────────────────────────────────
  const onReconnected = () => {
    log('[twilio:room:reconnected]', {
      t: Date.now(),
      roomSid,
      roomName: room.name ?? null,
      localParticipantSid: localSid,
    });
  };
  on(room as any, 'reconnected', onReconnected);

  // ── [twilio:network:dominant-speaker] ─────────────────────────────────────
  const onDominantSpeaker = (participant: { sid?: string; identity?: string } | null) => {
    log('[twilio:network:dominant-speaker]', {
      t: Date.now(),
      roomSid,
      participantSid: participant?.sid ?? null,
      identity: participant?.identity ?? null,
    });
  };
  on(room as any, 'dominantSpeakerChanged', onDominantSpeaker);

  // ── Local participant listeners ────────────────────────────────────────────
  const lp = room.localParticipant;

  const buildLocalTrackPayload = (pub: any) => {
    const track = pub.track ?? pub;
    return {
      t: Date.now(),
      roomSid,
      participantSid: lp.sid ?? null,
      trackSid: track?.sid ?? pub?.trackSid ?? null,
      trackName: track?.name ?? pub?.trackName ?? null,
      kind: track?.kind ?? pub?.kind ?? null,
      enabled: track?.isEnabled ?? null,
      isStarted: null,
      source: 'local' as const,
    };
  };

  // Emit for already-published tracks
  lp.tracks?.forEach((pub: any) => {
    log('[twilio:local:track:publish]', buildLocalTrackPayload(pub));
    emitCodec(pub.track ?? pub, roomSid, 'local');
  });

  const onTrackPublished = (pub: any) => {
    log('[twilio:local:track:publish]', buildLocalTrackPayload(pub));
    emitCodec(pub.track ?? pub, roomSid, 'local');
  };
  on(lp as any, 'trackPublished', onTrackPublished);

  const onTrackUnpublished = (pub: any) => {
    log('[twilio:local:track:unpublish]', buildLocalTrackPayload(pub));
  };
  on(lp as any, 'trackUnpublished', onTrackUnpublished);

  const onLocalTrackEnabled = (pub: any) => {
    log('[twilio:local:track:enable]', buildLocalTrackPayload(pub));
  };
  on(lp as any, 'trackEnabled', onLocalTrackEnabled);

  const onLocalTrackDisabled = (pub: any) => {
    log('[twilio:local:track:disable]', buildLocalTrackPayload(pub));
  };
  on(lp as any, 'trackDisabled', onLocalTrackDisabled);

  const onLocalTrackStopped = (pub: any) => {
    log('[twilio:local:track:stop]', buildLocalTrackPayload(pub));
  };
  on(lp as any, 'trackStopped', onLocalTrackStopped);

  // ── Local NQ ──────────────────────────────────────────────────────────────
  const onLocalNQ = (level: number) => {
    log('[twilio:network:quality]', {
      t: Date.now(),
      roomSid,
      participantSid: lp.sid ?? null,
      scope: 'local',
      level,
    });
  };
  on(lp as any, 'networkQualityLevelChanged', onLocalNQ);

  // ── Remote participant setup ───────────────────────────────────────────────

  function attachRemoteParticipant(participant: any): () => void {
    const participantCleanups: Array<() => void> = [];

    function onP<T>(
      emitter: T & { on(event: string, cb: (...args: any[]) => void): any; off(event: string, cb: (...args: any[]) => void): any },
      event: string,
      cb: (...args: any[]) => void
    ): void {
      emitter.on(event, cb);
      participantCleanups.push(() => emitter.off(event, cb));
    }

    log('[twilio:remote:participant:connect]', {
      t: Date.now(),
      roomSid,
      participantSid: participant.sid,
      identity: participant.identity,
    });

    const buildRemoteTrackPayload = (track: any) => ({
      t: Date.now(),
      roomSid,
      participantSid: participant.sid,
      trackSid: track?.sid ?? null,
      trackName: track?.name ?? null,
      kind: track?.kind ?? null,
      enabled: track?.isEnabled ?? null,
      isStarted: track?.isStarted ?? null,
      source: 'remote' as const,
    });

    // Emit for already-subscribed tracks
    participant.tracks?.forEach((pub: any) => {
      if (pub.track) {
        log('[twilio:remote:track:subscribe]', buildRemoteTrackPayload(pub.track));
        emitCodec(pub.track, roomSid, 'remote');
        attachTrackListeners(pub.track, participant, participantCleanups);
      }
    });

    // trackSubscribed
    const onTrackSubscribed = (track: any) => {
      log('[twilio:remote:track:subscribe]', buildRemoteTrackPayload(track));
      emitCodec(track, roomSid, 'remote');
      attachTrackListeners(track, participant, participantCleanups);
    };
    onP(participant, 'trackSubscribed', onTrackSubscribed);

    // trackUnsubscribed
    const onTrackUnsubscribed = (track: any) => {
      log('[twilio:remote:track:unsubscribe]', buildRemoteTrackPayload(track));
    };
    onP(participant, 'trackUnsubscribed', onTrackUnsubscribed);

    // trackEnabled
    const onRemoteTrackEnabled = (pub: any) => {
      const track = pub.track ?? pub;
      log('[twilio:remote:track:enable]', buildRemoteTrackPayload(track));
    };
    onP(participant, 'trackEnabled', onRemoteTrackEnabled);

    // trackDisabled
    const onRemoteTrackDisabled = (pub: any) => {
      const track = pub.track ?? pub;
      log('[twilio:remote:track:disable]', buildRemoteTrackPayload(track));
    };
    onP(participant, 'trackDisabled', onRemoteTrackDisabled);

    // Remote NQ
    const onRemoteNQ = (level: number) => {
      log('[twilio:network:quality]', {
        t: Date.now(),
        roomSid,
        participantSid: participant.sid,
        scope: 'remote',
        level,
      });
    };
    onP(participant, 'networkQualityLevelChanged', onRemoteNQ);

    return () => participantCleanups.forEach(fn => fn());
  }

  const remoteParticipantDetachers = new Map<string, () => void>();

  // Wire up pre-existing remote participants
  room.participants?.forEach((participant: any) => {
    const detach = attachRemoteParticipant(participant);
    remoteParticipantDetachers.set(participant.sid, detach);
  });

  // participantConnected
  const onParticipantConnected = (participant: any) => {
    const detach = attachRemoteParticipant(participant);
    remoteParticipantDetachers.set(participant.sid, detach);
  };
  on(room as any, 'participantConnected', onParticipantConnected);

  // participantDisconnected
  const onParticipantDisconnected = (participant: any) => {
    log('[twilio:remote:participant:disconnect]', {
      t: Date.now(),
      roomSid,
      participantSid: participant.sid,
      identity: participant.identity,
    });
    const detach = remoteParticipantDetachers.get(participant.sid);
    if (detach) {
      detach();
      remoteParticipantDetachers.delete(participant.sid);
    }
  };
  on(room as any, 'participantDisconnected', onParticipantDisconnected);

  // ── Detach function ────────────────────────────────────────────────────────
  return () => {
    cleanups.forEach(fn => fn());
    remoteParticipantDetachers.forEach(detach => detach());
    remoteParticipantDetachers.clear();
  };
}

// ── Per-track listeners (started/stopped) ─────────────────────────────────

function attachTrackListeners(
  track: any,
  participant: any,
  cleanups: Array<() => void>
): void {
  const payload = () => ({
    t: Date.now(),
    roomSid: null as string | null,
    participantSid: participant.sid,
    trackSid: track?.sid ?? null,
    trackName: track?.name ?? null,
    kind: track?.kind ?? null,
    enabled: track?.isEnabled ?? null,
    source: 'remote' as const,
  });

  const onStarted = () => {
    log('[twilio:remote:track:started]', { ...payload(), isStarted: true });
  };
  const onStopped = () => {
    log('[twilio:remote:track:stop]', { ...payload(), isStarted: false });
  };

  track.on('started', onStarted);
  track.on('stopped', onStopped);

  cleanups.push(() => {
    track.off('started', onStarted);
    track.off('stopped', onStopped);
  });
}

// ── Codec helper ─────────────────────────────────────────────────────────────

function emitCodec(track: any, roomSid: string | null, _source: 'local' | 'remote'): void {
  if (!track) return;
  const kind = track.kind;
  if (kind !== 'audio' && kind !== 'video') return;
  log('[twilio:codec]', {
    t: Date.now(),
    roomSid,
    trackSid: track.sid ?? null,
    kind,
    codec: track.codec ?? null,
  });
}
